// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TySupportStatus, TySupportTicket } from "@/types";
import { formatDateTime, pressToOpen } from "@/utils";
import Avatar from "@/UI/atoms/Avatar";
import Icon from "@/UI/atoms/Icon";
import StatusChip from "@/UI/atoms/StatusChip";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    ticket: TySupportTicket;
    /** The whole tile opens the ticket — the message and the answer are both read there. */
    onOpen: () => void;
    /** Absent for a ticket this person may not take off the list — see the screen. */
    onHide?: () => void;
};

/** Waiting on us, being dealt with, done. Only the last one is green. */
const TONES: Record<TySupportStatus, "reserved" | "accent" | "available"> = {
    pending: "reserved",
    contacted: "accent",
    solved: "available",
};

/**
 * One request for help, as a tile in a grid like every other list in this app.
 *
 * **The answer is a badge here, never the text.** A reply read off a tile sits in a column a third of
 * a screen wide, next to five other people's prose — so the tile reports that an answer exists and
 * the ticket is opened to read it. That is also the only reason this card is a navigation target:
 * there is a destination now, and it is a popup rather than a screen.
 */
export default function SupportTicketCard({ ticket, onOpen, onHide }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang) ?? "ar";

    const author = typeof ticket.client === "object" && ticket.client ? ticket.client : null;

    return (
        <article className="support-card" role="button" tabIndex={0} onClick={onOpen} onKeyDown={pressToOpen(onOpen)}>
            <header className="support-card__head">
                <h2 className="support-card__subject">{ticket.subject}</h2>
                <StatusChip size="sm" tone={TONES[ticket.status]} label={t.support.statuses[ticket.status]} />
            </header>

            {/* Enough to recognize which request this is; the rest is one press away. */}
            <p className="support-card__message">{ticket.message}</p>

            {/* That we answered, not what we answered — the answer is read whole or not at all. */}
            {ticket.reply && (
                <span className="support-card__answered">
                    <Icon name="check" size={14} />
                    <span>{t.support.answered}</span>
                </span>
            )}

            <footer className="support-card__foot">
                {author && <Avatar name={author.name} picture={author.picture ?? null} size={24} rounded />}
                <span className="support-card__by">{author?.name ?? ""}</span>
                <span className="support-card__when">{formatDateTime(ticket.createdAt, lang)}</span>

                {onHide && (
                    <button
                        type="button"
                        className="support-card__hide"
                        onClick={(event) => {
                            event.stopPropagation();
                            onHide();
                        }}
                        aria-label={t.support.hide}
                    >
                        <Icon name="trash" size={14} />
                    </button>
                )}
            </footer>
        </article>
    );
}
